import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ChartLine, Loader2, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import { parseUserFile, ACCEPTED_FILE_TYPES, ACCEPTED_HELP_TEXT } from "@/lib/file-parsing";
import {
  DEFAULT_TRAINING_PARAMS,
  categoricalColumns,
  numericColumns,
  trainClassification,
  trainClustering,
  trainRegression,
} from "@/lib/ml";
import { useModels } from "@/hooks/useModels";
import { useModelWorkspace } from "@/contexts/ModelWorkspaceContext";

type ModelType = "regression" | "classification" | "clustering";

interface RunOption {
  id: string;
  label: string;
}

export const ModelBuilder = () => {
  const { dataset, setDataset, markTrained } = useModelWorkspace();
  const { saveTrainedModel } = useModels();

  const [runs, setRuns] = useState<RunOption[]>([]);
  const [loadingSource, setLoadingSource] = useState(false);
  const [modelType, setModelType] = useState<ModelType>("regression");
  const [target, setTarget] = useState<string>("");
  const [features, setFeatures] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [isTraining, setIsTraining] = useState(false);

  const rows = dataset?.rows ?? [];
  const numeric = useMemo(() => numericColumns(rows), [rows]);
  const categorical = useMemo(() => categoricalColumns(rows), [rows]);
  const targetOptions = modelType === "classification" ? categorical : numeric;

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("workflow_runs")
        .select("id, input_source, created_at")
        .eq("user_id", user.id)
        .eq("status", "completed")
        .order("created_at", { ascending: false })
        .limit(10);
      setRuns(
        (data ?? []).map((r) => ({
          id: r.id,
          label: `${r.input_source || "Workflow data"} — ${new Date(r.created_at).toLocaleDateString()}`,
        })),
      );
    })();
  }, []);

  useEffect(() => {
    // Default the target and inputs to real columns from the loaded data.
    if (!targetOptions.length) {
      setTarget("");
      setFeatures([]);
      return;
    }
    const nextTarget = targetOptions.includes(target) ? target : targetOptions[0];
    setTarget(nextTarget);
    setFeatures(numeric.filter((c) => c !== nextTarget).slice(0, 6));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset?.label, modelType]);

  const handleFile = async (file: File) => {
    setLoadingSource(true);
    try {
      const parsed = await parseUserFile(file);
      if (!parsed.rows.length) throw new Error("That file has no readable rows.");
      setDataset({ label: file.name, rows: parsed.rows });
      toast.success(`Loaded ${parsed.rows.length} rows from ${file.name}`);
    } catch (e: any) {
      toast.error(e?.message || "Could not read that file");
    } finally {
      setLoadingSource(false);
    }
  };

  const loadRun = async (runId: string) => {
    setLoadingSource(true);
    try {
      const { data, error } = await supabase
        .from("workflow_data")
        .select("row_data")
        .eq("workflow_run_id", runId)
        .order("row_index", { ascending: true })
        .limit(5000);
      if (error) throw error;
      const parsedRows = (data ?? []).map((d) => d.row_data as Record<string, any>);
      if (!parsedRows.length) throw new Error("That run has no stored rows.");
      const label = runs.find((r) => r.id === runId)?.label ?? "Workflow data";
      setDataset({ label, rows: parsedRows });
      toast.success(`Loaded ${parsedRows.length} rows`);
    } catch (e: any) {
      toast.error(e?.message || "Could not load that run");
    } finally {
      setLoadingSource(false);
    }
  };

  const toggleFeature = (col: string) => {
    setFeatures((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col],
    );
  };

  const handleTrain = async () => {
    if (!rows.length) {
      toast.error("Load a file or a past run first.");
      return;
    }
    const usedFeatures = features.filter((f) => f !== target);
    if (!usedFeatures.length) {
      toast.error("Pick at least one input column.");
      return;
    }
    if (modelType !== "clustering" && !target) {
      toast.error("Pick the column you want to predict.");
      return;
    }

    setIsTraining(true);
    try {
      const trained =
        modelType === "regression"
          ? trainRegression(rows, target, usedFeatures, DEFAULT_TRAINING_PARAMS)
          : modelType === "classification"
            ? trainClassification(rows, target, usedFeatures, DEFAULT_TRAINING_PARAMS)
            : trainClustering(rows, usedFeatures, 3);

      const saved = await saveTrainedModel.mutateAsync({
        name: name.trim() || `${modelType} on ${dataset?.label ?? "data"}`,
        dataSource: dataset?.label ?? "uploaded data",
        parameters: { ...DEFAULT_TRAINING_PARAMS, model_type: modelType },
        trained,
        sampleRows: rows,
      });

      markTrained(saved.id);
      trackEvent("model_generated", { model_type: modelType });
      toast.success(
        modelType === "clustering"
          ? `Found ${trained.metrics.clusters} groups across ${trained.trainRows} rows`
          : `Trained on ${trained.trainRows} rows, tested on ${trained.testRows}`,
      );
    } catch (e: any) {
      toast.error(e?.message || "Training failed");
    } finally {
      setIsTraining(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-2">
        <ChartLine className="h-5 w-5" />
        <h2 className="text-xl font-semibold">Build a model</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Use your own data. Everything is trained and scored on the rows you load — no samples.
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">1. Your data</label>
            <label className="flex items-center gap-2 border border-dashed rounded-md p-3 cursor-pointer hover:bg-muted/50">
              <Upload className="h-4 w-4" />
              <span className="text-sm">
                {dataset ? `${dataset.label} (${rows.length} rows)` : "Upload a file"}
              </span>
              <input
                type="file"
                className="hidden"
                accept={ACCEPTED_FILE_TYPES}
                onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              />
            </label>
            <p className="text-xs text-muted-foreground mt-1">{ACCEPTED_HELP_TEXT}</p>

            {runs.length > 0 && (
              <div className="mt-3">
                <span className="text-xs text-muted-foreground">or use a past run</span>
                <Select onValueChange={loadRun}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a completed run" />
                  </SelectTrigger>
                  <SelectContent>
                    {runs.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">2. What should it do?</label>
            <Select onValueChange={(v: ModelType) => setModelType(v)} value={modelType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="regression">Predict a number (e.g. revenue)</SelectItem>
                <SelectItem value="classification">Predict a category (e.g. will churn)</SelectItem>
                <SelectItem value="clustering">Group similar records together</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {modelType !== "clustering" && (
            <div>
              <label className="text-sm font-medium mb-2 block">3. Column to predict</label>
              <Select onValueChange={setTarget} value={target} disabled={!targetOptions.length}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      rows.length ? "No suitable column found" : "Load your data first"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {targetOptions.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              {modelType === "clustering" ? "3." : "4."} Columns to learn from
            </label>
            {numeric.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {rows.length
                  ? "No numeric columns detected in this data."
                  : "Load your data to choose inputs."}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {numeric
                  .filter((c) => c !== target)
                  .map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleFeature(c)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        features.includes(c)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background hover:bg-muted"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Model name (optional)</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Q3 revenue forecast"
            />
          </div>

          <Button
            size="lg"
            className="w-full"
            onClick={handleTrain}
            disabled={isTraining || loadingSource || !rows.length}
          >
            {isTraining ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Training on your data...
              </>
            ) : (
              "Train model"
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
};
